import React, { useEffect, useRef, useState } from 'react';
import { Collapse, LinearProgress } from '@mui/material';
import SearchBar from '../../../../../components/SearchBar';
import useTranslate from '../../../../../../customHooks/useTranslate';
import IconButton from '../../../../../../muiComponents/IconButton';
import { useZitoneoHotelRooms } from '../../../../contexts/ZitoneoContentContext';
import { useZitoneoUI } from '../../../../contexts/ZitoneoUIContext';
import { commonIcons } from '../../../../../../utils/commonVariables/commonIcons';
import { useZitoneoServices } from '../../../../contexts/ZitoneoContentContext';
import {
    HotelRoomState,
    VisibleContentScope,
} from '../../../../types/zitoneoServicesTypes';

const RoomServicesMenu = () => {
    const translate = useTranslate();
    const { rooms } = useZitoneoHotelRooms();
    const { ComplementaryServiceTypes } = useZitoneoServices();
    const {
        selectedRoomId,
        handleSelectRoom,
        handleVisibleContentScope,
        visibleContentScope,
    } = useZitoneoUI();
    const selectedRoom = rooms.find(room => room.Id === selectedRoomId) || null;
    const [isMenuVisible, setIsMenuVisible] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const selectedRoomElementRef = useRef<HTMLLIElement | null>(null);

    const filteredRooms = rooms.filter(room =>
        [room.Label, room.MainSupplierHotelRoomName, room.MasterRoomName].some(
            value =>
                value.toLowerCase().includes(searchTerm.trim().toLowerCase())
        )
    );

    useEffect(() => {
        if (
            visibleContentScope === VisibleContentScope.ROOM &&
            rooms.length &&
            !selectedRoom
        ) {
            handleSelectRoom(rooms[0].Id);
        } else if (!rooms.length && selectedRoomId) {
            handleVisibleContentScope(VisibleContentScope.HOTEL);
        }
    }, [
        rooms,
        selectedRoom,
        selectedRoomId,
        visibleContentScope,
        handleSelectRoom,
        handleVisibleContentScope,
    ]);

    useEffect(() => {
        if (isMenuVisible && selectedRoomId) {
            selectedRoomElementRef.current?.scrollIntoView({
                block: 'nearest',
                behavior: 'smooth',
            });
        }
    }, [isMenuVisible, selectedRoomId]);

    const renderRoomItem = (room: HotelRoomState) => {
        return (
            <li
                key={room.Id}
                ref={selectedRoomId === room.Id ? selectedRoomElementRef : null}
                className={`rooms-menu__item categories-menu__list-item ${
                    selectedRoom?.Id === room.Id ? '--selected' : ''
                }`}
                onClick={() => {
                    setSearchTerm('');
                    handleSelectRoom(room.Id);
                }}
            >
                <div className='rooms-menu__main'>
                    <div className='rooms-menu__name-container'>
                        <p className='text-overflow-ellipsis'>{room.Label}</p>
                        <p className='white-space-nowrap'>
                            {room.Services.length} /{' '}
                            {ComplementaryServiceTypes.length}
                        </p>
                    </div>
                    <div className='rooms-menu__progress'>
                        <LinearProgress
                            variant='determinate'
                            className={
                                selectedRoomId === room.Id ? '--selected' : ''
                            }
                            value={
                                ComplementaryServiceTypes.length > 0
                                    ? (room.Services.length /
                                          ComplementaryServiceTypes.length) *
                                      100
                                    : 0
                            }
                        />
                    </div>
                </div>
            </li>
        );
    };

    return (
        <div
            className={`rooms-menu__container ${
                isMenuVisible ? '--expanded' : '--collapsed'
            }`}
        >
            <header className='rooms-menu__header'>
                <div className='flex flex-between flex-align-center'>
                    <h5 className='rooms-menu__title'>{translate('rooms')}</h5>
                    <IconButton
                        iconName={
                            isMenuVisible
                                ? commonIcons.ARROW_UP
                                : commonIcons.ARROW_DOWN
                        }
                        onClick={() => setIsMenuVisible(visible => !visible)}
                        aria-expanded={isMenuVisible}
                        size='small'
                    />
                </div>
                {isMenuVisible && (
                    <div className='rooms-menu__search'>
                        <SearchBar
                            value={searchTerm}
                            placeholder={translate('search_room')}
                            onChange={event =>
                                setSearchTerm(event.target.value)
                            }
                            onClear={() => setSearchTerm('')}
                        />
                    </div>
                )}
            </header>
            {!isMenuVisible && selectedRoom && (
                <div className='rooms-menu__content'>
                    <div className='rooms-menu__selected'>
                        <ul className='rooms-menu__list'>
                            {renderRoomItem(selectedRoom)}
                        </ul>
                    </div>
                </div>
            )}
            <Collapse
                className='rooms-menu__list-collapse'
                in={isMenuVisible}
                timeout='auto'
                unmountOnExit
            >
                <div className='rooms-menu__content'>
                    {filteredRooms.length ? (
                        <ul className='rooms-menu__list'>
                            {filteredRooms.map(renderRoomItem)}
                        </ul>
                    ) : (
                        <p className='rooms-menu__empty'>
                            {rooms.length
                                ? translate('no_results')
                                : translate('no_rooms_available')}
                        </p>
                    )}
                </div>
            </Collapse>
        </div>
    );
};

export default RoomServicesMenu;
